import printJS from 'print-js';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/common/Button';
import { LogoBox } from '../components/common/LogoBox';
import Timer from '../components/common/Timer';
import { Layout } from '../components/Layout/Layout';
import { useNavigation } from '../hooks/useNavigation';
import { setPaid } from '../store/authSlice';
import { resetPhotoState } from '../store/photoSlice';
import { RootState } from '../store/store';
import { getFourCutImageWithQrCode } from '../utils/imageProcessing';
import { useTimeoutHandler } from '../hooks/useTimeoutHandler.ts';
import { Container, QRCodeContainer, QRCode } from './PrintPage.styled';

const PRINT_DELAY = 2;

export function PrintPage() {
  const { t } = useTranslation();
  const { navigateToStart } = useNavigation();
  const dispatch = useDispatch();
  const { qrCode, fourCutImage, selectedFrameIndex, photoCount, allowQrcode } = useSelector(
    (state: RootState) => state.photo
  );
  const block = useSelector((state: RootState) => state.block.block);
  const selectedThemeId = useSelector((state: RootState) => state.theme.selectedThemeId);

  const theme = selectedThemeId
    ? block.themes.find((t) => t.id === selectedThemeId) || block.themes[0]
    : block.themes[0];

  const { frames } = theme;

  const [isPrinting, setIsPrinting] = useState(photoCount !== 0);
  const { handlePrintTimeout } = useTimeoutHandler();

  const handleFinish = () => {
    dispatch(resetPhotoState());
    localStorage.removeItem('isPaid');
    dispatch(setPaid(false));
    navigateToStart();
  };

  useEffect(() => {
    if (photoCount === 0 || !fourCutImage || frames.length === 0) {
      return;
    }

    const printWithDelay = (imageUrl: URL, delay: number, i: number = 0) => {
      setTimeout(() => {
        printJS({
          printable: imageUrl,
          type: 'image',
          style: `
          @page { margin: 0; size: auto; }
          body { margin: 0; }
          img {
            max-width: 100%;
            max-height: 100vh;
            object-fit: contain;
            display: block;
            margin: auto;
          }
        `,
        });

        if (i < photoCount - 1) {
          printWithDelay(imageUrl, delay, i + 1);
        } else {
          setIsPrinting(false);
        }
      }, delay * 1000);
    };

    if (allowQrcode) {
      if (qrCode) {
        getFourCutImageWithQrCode(
          fourCutImage,
          qrCode,
          frames[selectedFrameIndex].printQrCodePosition
        ).then((fourCutImageWithQrCode) => {
          const imageUrl = new URL(fourCutImageWithQrCode);
          printWithDelay(imageUrl, PRINT_DELAY);
        });
      }
    } else {
      const imageUrl = new URL(fourCutImage);
      printWithDelay(imageUrl, PRINT_DELAY);
    }
  }, [qrCode, allowQrcode]);

  const shouldPauseTimer = !fourCutImage || (allowQrcode && !qrCode);

  return (
    <Layout>
      <Timer initialTime={75} onTimeout={handlePrintTimeout} isPaused={shouldPauseTimer} />
      <Container>
        <LogoBox />
        <QRCodeContainer>
          <div>
            {isPrinting
              ? t('print.printing')
              : photoCount === 0
                ? ''
                : t('print.started')}
          </div>
          {allowQrcode ? (
            qrCode ? (
              <>
                <QRCode src={qrCode} alt="QR Code" />
                {!isPrinting && <Button onClick={handleFinish}>{t('print.finish')}</Button>}
              </>
            ) : (
              <>
                <p>{t('print.qrCodeComing')}</p>
                <p>{t('print.downloadInfo')}</p>
              </>
            )
          ) : (
            <>
              {!isPrinting && (
                <Button
                  style={{
                    marginTop: 'calc(320px + 36px + 32px)',
                  }}
                  onClick={handleFinish}
                >
                  {t('print.finish')}
                </Button>
              )}
            </>
          )}
        </QRCodeContainer>
      </Container>
    </Layout>
  );
}
